import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import Shepherd, { Tour } from 'shepherd.js';
import { BehaviorSubject, Subscription, fromEvent } from 'rxjs';

export type PageTourEndReason =
  | 'action'
  | 'cancel'
  | 'finish'
  | 'navigation'
  | 'registration'
  | 'resize';

export interface PageTourStep {
  id: string;
  text: string;
  target: string;
  actionLabel: string;
  action: () => void;
  prepare?: () => void | Promise<void>;
  /** Intercept a click on the highlighted target only when it is this action. */
  activateTarget?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

export interface PageTourRegistration {
  id: string;
  title: string;
  getSteps: () => PageTourStep[];
  cleanup?: (reason: PageTourEndReason) => void;
  onStart?: () => void;
}

export interface PageTourRegistrationToken {
  readonly id: symbol;
}

export interface PageTourMobileState {
  title: string;
  steps: PageTourStep[];
}

interface ActiveTour {
  registrationToken: PageTourRegistrationToken;
  registration: PageTourRegistration;
  focusReturn: HTMLElement | null;
  mode: 'desktop' | 'mobile';
  tour?: Tour;
}

@Injectable({ providedIn: 'root' })
export class PageTourService implements OnDestroy {
  private registration: {
    token: PageTourRegistrationToken;
    value: PageTourRegistration;
  } | null = null;
  private active: ActiveTour | null = null;
  private targetCleanup?: () => void;
  private direction: 'forward' | 'backward' = 'forward';
  private readonly subscriptions = new Subscription();
  private readonly registrationSubject =
    new BehaviorSubject<PageTourRegistration | null>(null);
  private readonly mobileStateSubject =
    new BehaviorSubject<PageTourMobileState | null>(null);

  readonly registration$ = this.registrationSubject.asObservable();
  readonly mobileState$ = this.mobileStateSubject.asObservable();

  get currentRegistration(): PageTourRegistration | null {
    return this.registration?.value ?? null;
  }

  get mobileState(): PageTourMobileState | null {
    return this.mobileStateSubject.value;
  }

  constructor(
    private router: Router,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.subscriptions.add(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationStart) {
          this.cancel('navigation');
        }
      })
    );
    this.subscriptions.add(
      fromEvent(window, 'resize').subscribe(() => {
        if (!this.active) return;
        const nextMode = this.isMobileViewport() ? 'mobile' : 'desktop';
        if (nextMode !== this.active.mode) {
          this.cancel('resize');
        }
      })
    );
  }

  register(value: PageTourRegistration): PageTourRegistrationToken {
    const token = { id: Symbol(value.id) };
    if (this.registration) {
      this.cancel('registration');
    }
    this.registration = { token, value };
    this.registrationSubject.next(value);
    return token;
  }

  unregister(token: PageTourRegistrationToken): void {
    if (this.registration?.token !== token) return;
    this.cancel('registration');
    this.registration = null;
    this.registrationSubject.next(null);
  }

  start(focusReturn: HTMLElement | null = null): boolean {
    const registered = this.registration;
    if (!registered) return false;

    this.cancel('registration', false);
    const steps = registered.value.getSteps();
    if (!steps.length) return false;

    this.active = {
      registrationToken: registered.token,
      registration: registered.value,
      focusReturn,
      mode: this.isMobileViewport() ? 'mobile' : 'desktop',
    };
    registered.value.onStart?.();

    if (this.active.mode === 'mobile') {
      this.mobileStateSubject.next({
        title: registered.value.title,
        steps,
      });
      requestAnimationFrame(() => {
        this.document
          .querySelector<HTMLElement>('[data-page-tour-sheet-heading]')
          ?.focus();
      });
      return true;
    }

    this.startDesktopTour(registered.token, registered.value, steps);
    return true;
  }

  runMobileAction(step: PageTourStep): void {
    const active = this.active;
    if (!active || active.mode !== 'mobile' || !this.mobileState) return;
    this.finishActive('action', false);
    this.ngZone.run(step.action);
  }

  cancel(
    reason: Exclude<PageTourEndReason, 'action' | 'finish'> = 'cancel',
    restoreFocus = true
  ): void {
    const active = this.active;
    if (!active) return;
    const tour = active.tour;
    this.finishActive(reason, restoreFocus);
    void tour?.cancel();
  }

  ngOnDestroy(): void {
    this.cancel('registration', false);
    this.subscriptions.unsubscribe();
    this.registrationSubject.complete();
    this.mobileStateSubject.complete();
  }

  private startDesktopTour(
    registrationToken: PageTourRegistrationToken,
    registration: PageTourRegistration,
    steps: PageTourStep[]
  ): void {
    const tour = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'cx-page-tour',
        cancelIcon: { enabled: true, label: 'Close tour' },
        canClickTarget: true,
        modalOverlayOpeningRadius: 4,
        scrollTo: { behavior: 'smooth', block: 'center' },
      },
    });
    if (!this.active || this.active.registrationToken !== registrationToken) {
      return;
    }
    this.active.tour = tour;
    this.direction = 'forward';

    steps.forEach((step, index) => {
      const buttons = [];
      if (index > 0) {
        buttons.push({
          text: 'Back',
          secondary: true,
          classes: 'cx-page-tour__button cx-page-tour__button--tertiary',
          action: () => {
            this.direction = 'backward';
            tour.back();
          },
        });
      }
      buttons.push({
        text: index === steps.length - 1 ? 'Finish' : 'Next',
        classes: 'cx-page-tour__button cx-page-tour__button--secondary',
        action: () => {
          this.direction = 'forward';
          if (index === steps.length - 1) tour.complete();
          else tour.next();
        },
      });
      buttons.push({
        text: step.actionLabel,
        classes: 'cx-page-tour__button cx-page-tour__button--primary',
        action: () => this.handoffDesktopAction(tour, step.action),
      });

      tour.addStep({
        id: step.id,
        text: step.text,
        attachTo: {
          element: step.target,
          on: step.placement ?? 'bottom',
        },
        buttons,
        beforeShowPromise: () => this.prepareStep(step, tour),
        when: {
          show: () => this.onStepShown(step, index, steps.length, tour),
          hide: () => this.clearTargetHandler(),
        },
      });
    });

    tour.on('complete', () => this.onDesktopEnded(tour, 'finish'));
    tour.on('cancel', () => this.onDesktopEnded(tour, 'cancel'));
    requestAnimationFrame(() => {
      if (this.active?.tour === tour) void tour.start();
    });
  }

  private async prepareStep(step: PageTourStep, tour: Tour): Promise<void> {
    await step.prepare?.();
    const target = await this.waitForTarget(step.target, tour);
    if (this.active?.tour !== tour) {
      // Shepherd continues its internal show after the promise resolves.
      await new Promise<void>(() => undefined);
    } else if (!target) {
      tour.getById(step.id)?.updateStepOptions({ attachTo: { element: null } });
    } else {
      tour.getById(step.id)?.updateStepOptions({
        attachTo: { element: target, on: step.placement ?? 'bottom' },
      });
    }
  }

  private async waitForTarget(
    selector: string,
    tour: Tour
  ): Promise<HTMLElement | null> {
    for (let attempt = 0; attempt < 45; attempt++) {
      if (this.active?.tour !== tour) return null;
      const target = this.document.querySelector<HTMLElement>(selector);
      if (target?.isConnected && target.getClientRects().length) return target;
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => resolve())
      );
    }
    return null;
  }

  private onStepShown(
    step: PageTourStep,
    index: number,
    totalSteps: number,
    tour: Tour
  ): void {
    this.clearTargetHandler();
    const target = this.document.querySelector<HTMLElement>(step.target);
    if (!target?.isConnected || !target.getClientRects().length) {
      queueMicrotask(() => this.skipMissingStep(tour, index, totalSteps));
      return;
    }
    if (!step.activateTarget) return;

    const onClick = (event: MouseEvent) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.handoffDesktopAction(tour, step.action);
    };
    target.addEventListener('click', onClick, true);
    this.targetCleanup = () =>
      target.removeEventListener('click', onClick, true);
  }

  private skipMissingStep(tour: Tour, index: number, totalSteps: number): void {
    if (this.active?.tour !== tour) return;
    if (this.direction === 'backward') {
      if (index === 0) tour.cancel();
      else tour.back();
    } else if (index === totalSteps - 1) {
      tour.complete();
    } else {
      tour.next();
    }
  }

  private handoffDesktopAction(tour: Tour, action: () => void): void {
    if (this.active?.tour !== tour) return;
    this.finishActive('action', false);
    void Promise.resolve(tour.cancel()).then(() => this.ngZone.run(action));
  }

  private onDesktopEnded(
    tour: Tour,
    reason: Extract<PageTourEndReason, 'cancel' | 'finish'>
  ): void {
    if (this.active?.tour !== tour) return;
    this.finishActive(reason, true);
  }

  private finishActive(reason: PageTourEndReason, restoreFocus: boolean): void {
    const active = this.active;
    if (!active) return;
    this.active = null;
    this.mobileStateSubject.next(null);
    this.clearTargetHandler();
    active.registration.cleanup?.(reason);
    if (restoreFocus) {
      requestAnimationFrame(() => {
        if (active.focusReturn?.isConnected) active.focusReturn.focus();
      });
    }
  }

  private clearTargetHandler(): void {
    this.targetCleanup?.();
    this.targetCleanup = undefined;
  }

  private isMobileViewport(): boolean {
    return window.innerWidth < 1024;
  }
}
